/**
 * Date formatting utilities that respect user timezone preferences.
 * Uses Intl.DateTimeFormat for timezone-aware formatting.
 */
import { getTimezoneOptions as getTimezoneOptionsFromUtility } from './timezones';

const BARE_ISO_UTC_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?$/;
const ISO_WITH_TIMEZONE_REGEX = /(Z|[+-]\d{2}:\d{2})$/i;

/** How timestamps are rendered across the app. */
export type DateDisplayMode = 'relative' | 'absolute' | 'both';

export const LOCALE_STORAGE_KEY = 'user-locale';
export const TIMEZONE_STORAGE_KEY = 'user-timezone';
export const DATE_DISPLAY_MODE_STORAGE_KEY = 'user-date-display-mode';
export const DEFAULT_DATE_DISPLAY_MODE: DateDisplayMode = 'relative';

/**
 * Normalizes a potential UTC ISO timestamp string.
 * If a timestamp has no timezone suffix, we treat it as UTC and append `Z`.
 */
export function normalizeUtcDateString(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return value;

  if (!trimmed.includes('T')) return value;
  if (ISO_WITH_TIMEZONE_REGEX.test(trimmed)) return value;
  if (BARE_ISO_UTC_REGEX.test(trimmed)) return `${trimmed}Z`;

  return value;
}

/**
 * Safely parse a date input where bare ISO timestamps are treated as UTC.
 */
export function parseUtcDate(
  date: string | Date | null | undefined,
): Date | null {
  if (!date) return null;

  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }

  const normalized = normalizeUtcDateString(date);
  const parsed = new Date(normalized);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** Get the user's preferred timezone, falling back to browser timezone */
export function getUserTimezone(): string {
  // Check localStorage for user preference (set from profile)
  const stored = localStorage.getItem(TIMEZONE_STORAGE_KEY);
  if (stored) return stored;
  
  // Fall back to browser timezone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/** Get the user's preferred locale, falling back to en-US */
export function getUserLocale(): string {
  return localStorage.getItem(LOCALE_STORAGE_KEY) || 'en-US';
}

/** Set the user's timezone preference */
export function setUserTimezone(timezone: string) {
  localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone);
}

/** Set the user's locale preference */
export function setUserLocale(locale: string) {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

/** Get the user's preferred timestamp display mode, falling back to 'relative' */
export function getUserDateDisplayMode(): DateDisplayMode {
  const stored = localStorage.getItem(DATE_DISPLAY_MODE_STORAGE_KEY);
  return stored === 'absolute' || stored === 'both' || stored === 'relative'
    ? stored
    : DEFAULT_DATE_DISPLAY_MODE;
}

/** Set the user's timestamp display mode preference */
export function setUserDateDisplayMode(mode: DateDisplayMode) {
  localStorage.setItem(DATE_DISPLAY_MODE_STORAGE_KEY, mode);
}

/** Format a date string or Date object to localized date+time */
export function formatDateTime(date: string | Date | null | undefined): string {
  const d = parseUtcDate(date);
  if (!d) return '—';
  
  return d.toLocaleString(getUserLocale(), {
    timeZone: getUserTimezone(),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Format a date string or Date to localized date only */
export function formatDate(date: string | Date | null | undefined): string {
  const d = parseUtcDate(date);
  if (!d) return '—';
  
  return d.toLocaleDateString(getUserLocale(), {
    timeZone: getUserTimezone(),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Format a date to relative time (e.g., "5 minutes ago") */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  const d = parseUtcDate(date);
  if (!d) return '—';
  
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  
  return formatDate(d);
}

/** Format time only */
export function formatTime(date: string | Date | null | undefined): string {
  const d = parseUtcDate(date);
  if (!d) return '—';
  
  return d.toLocaleTimeString(getUserLocale(), {
    timeZone: getUserTimezone(),
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Get list of common timezones for the dropdown selector */
export function getTimezoneOptions(): { value: string; label: string }[] {
  return getTimezoneOptionsFromUtility().map(({ value, label }) => ({ value, label }));
}
